// shop-frontend/src/components/ShopLayout.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import ShopSidebar from "./ShopSidebar";
import ShopNotificationsBell from "./ShopNotificationsBell";
import GlobalSearch from "./GlobalSearch";
import OrderNotifications from "./OrderNotifications";
import "../styles/shopLayout.css";

export default function ShopLayout() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop: remember collapsed preference
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () => localStorage.getItem("shopSidebarCollapsed") === "1"
  );

  const isMobile = useIsMobile(900);

  // Close drawer on route change (mobile)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = drawerOpen ? "hidden" : "";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen, isMobile]);

  // ── Swipe right to open drawer (mobile/tablet) ──
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    // Ignore very left edge (< 15px) to avoid iOS back gesture
    if (t.clientX < 15) { touchStartX.current = null; return; }
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = Math.abs(t.clientY - touchStartY.current);
    // Swipe right at least 60px, more horizontal than vertical
    if (dx > 60 && dx > dy * 1.5 && !drawerOpen) {
      setDrawerOpen(true);
    }
    touchStartX.current = null;
  }, [drawerOpen]);

  useEffect(() => {
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);

  // Desktop toggle
  const handleDesktopToggle = (collapsed) => {
    setDesktopCollapsed(collapsed);
    localStorage.setItem("shopSidebarCollapsed", collapsed ? "1" : "0");
  };

  return (
    <div className={`sl-wrap ${!isMobile && desktopCollapsed ? "sl-collapsed-mode" : ""} ${isMobile ? "sl-mobile-mode" : "sl-desktop-mode"}`}>
      <OrderNotifications />

      {/* Desktop-only toggle button */}
      {!isMobile && (
        <button
          type="button"
          className="sl-sidebar-toggle"
          onClick={() => handleDesktopToggle(!desktopCollapsed)}
          aria-label="Toggle sidebar"
          title={desktopCollapsed ? "Open menu" : "Close menu"}
        >
          {desktopCollapsed ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </button>
      )}

      {/* Mobile header — no hamburger, just branding */}
      {isMobile && (
        <div className="sl-mobile-header">
          <div className="sl-mobile-logo">
            <img src="/logo192.png" alt="Moondala" className="ss-logo-img" style={{ width: 28, height: 28 }} />
            <span className="sl-mobile-title">Moondala Shop</span>
          </div>
          <ShopNotificationsBell />
        </div>
      )}

      {/* Drawer overlay (mobile) — tap to close */}
      {isMobile && (
        <div
          className={`sl-drawer-overlay ${drawerOpen ? "sl-drawer-overlay--open" : ""}`}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <ShopSidebar
        collapsed={isMobile ? !drawerOpen : desktopCollapsed}
        onToggle={isMobile ? () => setDrawerOpen(false) : handleDesktopToggle}
        isMobile={isMobile}
        isDrawer={isMobile}
        drawerOpen={drawerOpen}
      />

      <main className="sl-main">
        <div className="sl-inner">
          {/* Topbar — desktop only (mobile has it in header) */}
          {!isMobile && (
            <div className="sl-topbar">
              <GlobalSearch />
              <ShopNotificationsBell />
            </div>
          )}

          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Hook: returns true when viewport <= breakpoint */
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}
